import { useCallback, useMemo } from 'react'

/**
 * Hook to manage selection state for the stock table.
 * @param {Array} filteredData - Currently displayed data items.
 * @param {Object} selections - Filter state including 'selectedStocks' Set.
 * @param {Function} setSelections - State updater function for selections.
 */
export default function useSelection(filteredData, selections, setSelections) {
    const selectedStocks = useMemo(
        () => selections.selectedStocks || new Set(),
        [selections.selectedStocks]
    )

    const filteredStockSet = useMemo(
        () => new Set(filteredData.map((item) => item.stock_no)),
        [filteredData]
    )

    const selectedInViewCount = useMemo(() => {
        let count = 0
        selectedStocks.forEach((stockNo) => {
            if (filteredStockSet.has(stockNo)) count += 1
        })
        return count
    }, [filteredStockSet, selectedStocks])

    const isAllSelected = useMemo(() =>
        filteredData.length > 0 && selectedInViewCount === filteredData.length,
    [filteredData.length, selectedInViewCount])

    const isSomeSelected = useMemo(() =>
        selectedInViewCount > 0 && !isAllSelected,
    [selectedInViewCount, isAllSelected])

    const handleSelectAll = useCallback(() => {
        const newSelectedStocks = new Set(selectedStocks)
        if (isAllSelected || isSomeSelected) {
            filteredData.forEach(item => newSelectedStocks.delete(item.stock_no))
        } else {
            filteredData.forEach(item => newSelectedStocks.add(item.stock_no))
        }
        setSelections(prev => ({ ...prev, selectedStocks: newSelectedStocks }))
    }, [filteredData, selectedStocks, isAllSelected, isSomeSelected, setSelections])

    const handleToggle = useCallback((stockNo) => {
        const newSelectedStocks = new Set(selectedStocks)
        if (newSelectedStocks.has(stockNo)) {
            newSelectedStocks.delete(stockNo)
        } else {
            newSelectedStocks.add(stockNo)
        }
        setSelections(prev => ({ ...prev, selectedStocks: newSelectedStocks }))
    }, [selectedStocks, setSelections])

    const isSelected = useCallback((stockNo) => selectedStocks.has(stockNo), [selectedStocks])

    return { 
        isAllSelected, 
        isSomeSelected, 
        handleSelectAll, 
        handleToggle,
        isSelected
    }
}
